import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Spin, Alert, Typography, Card, Tag, Divider } from "antd";
import {
  ArrowLeftOutlined,
  GlobalOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useTenantQuery, useTenantBillingQuery } from "../../Services";
import { ROUTE } from "../../Constant/AppConstants";
import { TenantBillingStatus } from "../../Enums";
import {
  TenantActionsGrid,
  TenantConfigDrawer,
  TenantDetailsCard,
  TenantFeatureDrawer,
  TenantPaymentDrawer,
  PrinterManageDrawer,
  StoreManageDrawer,
  WhatsAppStatsSection,
  WebsiteOrdersSection,
  TenantPackageDrawer,
} from "./components";

const { Title, Text } = Typography;

export const TenantDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [featureDrawerOpen, setFeatureDrawerOpen] = useState(false);
  const [storesDrawerOpen, setStoresDrawerOpen] = useState(false);
  const [printersDrawerOpen, setPrintersDrawerOpen] = useState(false);
  const [paymentsDrawerOpen, setPaymentsDrawerOpen] = useState(false);
  const [packageDrawerOpen, setPackageDrawerOpen] = useState(false);

  const { data: tenant, isLoading, isError } = useTenantQuery(id);
  const { data: billing } = useTenantBillingQuery(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-80">
        <Spin size="large" tip="Loading tenant info..." />
      </div>
    );
  }

  if (isError || !tenant) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <Alert
          message="Tenant not found"
          description="This tenant could not be loaded."
          type="error"
          showIcon
        />
        <Button
          className="mt-4"
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            navigate(`/system-admin/${ROUTE.system_admin_tenants}`)
          }
        >
          Back to tenants
        </Button>
      </div>
    );
  }

  const isActive = tenant.status === "active";

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="mx-auto space-y-6">
        <Card className="rounded-2xl border-gray-100 shadow-sm overflow-hidden">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() =>
                  navigate(`/system-admin/${ROUTE.system_admin_tenants}`)
                }
                className="!px-2 !mt-1 hover:!bg-gray-100 !rounded-lg"
              >
                Back
              </Button>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                  <Title level={3} className="!mb-0 !text-gray-900">
                    {tenant.name}
                  </Title>
                  <Tag
                    color={isActive ? "green" : "red"}
                    icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}
                    className="!rounded-full !px-3 !py-0.5 !text-xs !font-medium !border-0"
                  >
                    {isActive ? "Active" : "Inactive"}
                  </Tag>
                  {billing?.status === TenantBillingStatus.TERMINATED && (
                    <Tag
                      color="volcano"
                      className="!rounded-full !px-3 !py-0.5 !text-xs !font-medium !border-0"
                    >
                      Terminated
                    </Tag>
                  )}
                  {billing?.status === TenantBillingStatus.OVERDUE && (
                    <Tag
                      color="red"
                      className="!rounded-full !px-3 !py-0.5 !text-xs !font-medium !border-0"
                    >
                      Overdue
                    </Tag>
                  )}
                  {billing?.status === TenantBillingStatus.REMINDER && (
                    <Tag
                      color="gold"
                      className="!rounded-full !px-3 !py-0.5 !text-xs !font-medium !border-0"
                    >
                      Due in {billing.days_until_due}d
                    </Tag>
                  )}
                  {billing?.status === TenantBillingStatus.PAID && (
                    <Tag
                      color="blue"
                      className="!rounded-full !px-3 !py-0.5 !text-xs !font-medium !border-0"
                    >
                      Paid
                    </Tag>
                  )}
                </div>
                <div className="flex items-center gap-2.5 text-sm text-gray-500 flex-wrap">
                  <span className="inline-flex items-center gap-1.5">
                    <GlobalOutlined className="text-gray-400" />
                    <span className="font-mono text-gray-600">
                      {tenant.tenant_sub_name}
                    </span>
                  </span>
                  <Divider type="vertical" className="!mx-0 !bg-gray-200" />
                  <span className="font-mono text-xs">
                    ID: {tenant.id?.slice(0, 8)}...
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 pl-11 md:pl-0">
              <CalendarOutlined className="text-gray-400" />
              <div>
                <div className="text-xs text-gray-500">Created</div>
                <div className="text-sm font-semibold text-gray-800">
                  {tenant.created_at
                    ? new Date(tenant.created_at).toLocaleDateString()
                    : "-"}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <TenantActionsGrid
          onOpenConfig={() => setConfigDrawerOpen(true)}
          onOpenFeatures={() => setFeatureDrawerOpen(true)}
          onOpenStores={() => setStoresDrawerOpen(true)}
          onOpenPrinters={() => setPrintersDrawerOpen(true)}
          onOpenPayments={() => setPaymentsDrawerOpen(true)}
          onOpenPackage={() => setPackageDrawerOpen(true)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <TenantDetailsCard tenant={tenant} />
          </div>
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center gap-2 text-gray-400">
              <ClockCircleOutlined />
              <Text
                type="secondary"
                className="text-xs font-medium uppercase tracking-wide"
              >
                Activity &amp; Statistics
              </Text>
            </div>
            <WebsiteOrdersSection />
            <WhatsAppStatsSection />
          </div>
        </div>

        <TenantConfigDrawer
          open={configDrawerOpen}
          tenantId={id!}
          onClose={() => setConfigDrawerOpen(false)}
        />
        <TenantFeatureDrawer
          open={featureDrawerOpen}
          tenantId={id!}
          onClose={() => setFeatureDrawerOpen(false)}
        />
        <StoreManageDrawer
          open={storesDrawerOpen}
          tenantId={id}
          onClose={() => setStoresDrawerOpen(false)}
        />
        <PrinterManageDrawer
          open={printersDrawerOpen}
          tenantId={id}
          onClose={() => setPrintersDrawerOpen(false)}
        />
        <TenantPaymentDrawer
          open={paymentsDrawerOpen}
          tenantId={id}
          onClose={() => setPaymentsDrawerOpen(false)}
          onAssignPackage={() => setPackageDrawerOpen(true)}
        />
        <TenantPackageDrawer
          open={packageDrawerOpen}
          tenantId={id}
          onClose={() => setPackageDrawerOpen(false)}
        />
      </div>
    </div>
  );
};
